<template>
    <b-row>
        <b-col cols="12">
            <b-form-group>
                <v-select @search="fetchGroupMembers" v-model="selected"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" label="fullname" :options="members" multiple
                    class="select-size-lg modern-select p-1"
                    :placeholder="$t('Search members...')"
                >
                    <template #open-indicator="{ attributes }">
                        <feather-icon v-bind="attributes" icon="ChevronDownIcon" size="18" />
                    </template>
                    
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center px-2 py-1 border-bottom" 
                             v-if="members.length > 0">
                            <small class="text-muted">
                                {{ selected.length }} / {{ members.length }} {{ $t('selected') }}
                            </small>
                            <a href="javascript:void(0)" 
                               @click="toggleSelectAll"
                               class="text-primary"
                               style="font-size: 0.875rem;">
                                {{ isAllSelected ? $t('Clear all') : $t('Select all') }}
                            </a>
                        </div>
                    </template>
                    
                    <template #option="{ photo, fullname }">
                        <b-avatar :src="photo" class="mr-1" size="32" />
                        <span> {{ fullname }}</span>
                    </template>
                    <template #selected-option="{ photo, fullname }">
                        <b-avatar :src="photo" class="mr-1" size="24" />
                        <span> {{ fullname }}</span>
                    </template>
                </v-select>
            </b-form-group>
        </b-col>
    </b-row>
</template>

<script>
import { BRow, BCol, BFormGroup, BAvatar, BFormCheckbox } from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import axios from '@axios'
export default {
    components: {
        BRow,
        BCol,
        BFormGroup,
        vSelect,
        BAvatar,
        BFormCheckbox,
    },
    created() {
        // Fetch members when component is created
        if (this.searchOptions) {
            if (Array.isArray(this.searchOptions)) {
                // searchOptions is a list of groups
                this.fetchMultipleGroupMembers(this.searchOptions)
            } else if (this.searchOptions.id) {
                // searchOptions is a single group object
                this.fetchGroupMembers(this.searchOptions.id, () => {})
            }
        }
    },
    props: {
        initialMembers: {
            type: Array,
            default: () => []
        },
        searchOptions: {
            type: [Object, Array],
            default: () => ({})
        },
        searchType: {
            type: String,
            default: null
        }
    },


    methods: {
        toggleSelectAll() {
            this.selected = this.isAllSelected ? [] : [...this.members]
        },
        
        async fetchMultipleGroupMembers(groupList) {
            // Reset members array
            this.members = [];
            // Collect all promises
            const promises = groupList.map(group => {
                const groupId = group.id || group;
                return this.fetchGroupMembers(groupId, () => {})
                    .catch(error => {
                        console.error(`Error fetching members for group ${groupId}:`, error);
                        return []; // Return empty array on error
                    });
            });
            
            try {
                // Wait for all requests to complete
                const results = await Promise.all(promises);
                
                // Flatten and combine all results
                const allMembers = results.flat();
                
                // Remove duplicates based on user ID
                const uniqueMembers = allMembers.reduce((acc, member) => {
                    const exists = acc.find(m => m.userguid === member.userguid || m.id === member.id);
                    if (!exists) {
                        acc.push(member);
                    }
                    return acc;
                }, []);
                
                this.members = uniqueMembers;
            } catch (error) {
                console.error('Error fetching multiple group members:', error);
                this.members = [];
            }
        },
        
        fetchGroupMembers(search, loading = () => {}) {
            return new Promise((resolve, reject) => {
                var uri = `/admin/f/cm/groupmembers/${search}`;
                if (search ) {
                    loading(true);
                    axios
                        .get(uri)
                        .then((response) => {
                            var data = response.data;
                            var responseData = JSON.parse(JSON.stringify(data));
                            
                            // Handle different response structures
                            let membersList = [];
                            if (responseData.users) {
                                membersList = responseData.users;
                            } else if (responseData.data) {
                                membersList = responseData.data;
                            } else if (Array.isArray(responseData)) {
                                membersList = responseData;
                            }

                            this.members = membersList;

                            loading(false);
                            resolve(membersList);
                        })
                        .catch(error => {
                            loading(false);
                            reject(error);
                        })
                } else {
                    resolve([]);
                }
            })
        },

    },
    watch: {
        selected: function (newVal, oldVal) {
            this.$emit("selectionChanged", newVal);

        },
        initialMembers: function (newVal, oldVal) {
            this.selected = newVal;

        },
        searchOptions: {
            handler(newVal, oldVal) {
                // When searchOptions changes, fetch members for the new group(s)
                if (newVal) {
                    if (Array.isArray(newVal)) {
                        // searchOptions is a list of groups
                        this.fetchMultipleGroupMembers(newVal)
                    } else if (newVal.id) {
                        // searchOptions is a single group object
                        this.fetchGroupMembers(newVal.id, () => {})
                    }
                }
            },
            deep: true,
            immediate: false
        }
    },
    computed: {
        isAllSelected() {
            return this.members.length > 0 && this.selected.length === this.members.length
        },
        selectAllCheckbox: {
            get() {
                return this.members.length > 0 && this.selected.length === this.members.length
            },
            set(value) {
                // Setter is handled by @change event
            }
        }
    },
    data() {
        return {
            members: [],
            options: [],
            selected: this.initialMembers,
        }
    }
}
</script>

<style lang="scss" scoped>
.modern-select {
  ::v-deep {
    .vs__dropdown-toggle {
      border: 2px solid #e8e8e8;
      border-radius: 8px;
      padding: 8px 12px;
      transition: all 0.3s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
      
      &:hover {
        border-color: #7367f0;
        box-shadow: 0 4px 8px rgba(115, 103, 240, 0.1);
      }
    }
    
    .vs__dropdown-toggle--focused {
      border-color: #7367f0 !important;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
    }
    
    .vs__search {
      font-size: 1rem;
      padding: 4px 0;
      
      &::placeholder {
        color: #b4b4b4;
      }
    }
    
    .vs__dropdown-menu {
      border: 2px solid #e8e8e8;
      border-radius: 8px;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
      margin-top: 8px;
      max-height: 320px !important;
      overflow-y: auto;
      animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      transform-origin: top;
      will-change: transform, opacity;
      
      // Custom scrollbar
      &::-webkit-scrollbar {
        width: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 0 8px 8px 0;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #7367f0;
        border-radius: 4px;
        
        &:hover {
          background: #5e50ee;
        }
      }
    }
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px) scaleY(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scaleY(1);
      }
    }
    
    .vs__dropdown-option {
      padding: 8px 12px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      
      &--highlight {
        background: linear-gradient(118deg, rgba(115, 103, 240, 0.1), rgba(115, 103, 240, 0.05));
        color: #7367f0;
      }
      
      &:hover {
        transform: translateX(4px);
      }
    }
    
    .vs__selected {
      margin: 2px;
      padding: 4px 8px;
      border-radius: 6px;
      background-color: #7367f0;
      color: white;
      border: none;
    }
    
    .vs__deselect {
      fill: white;
    }
    
    .vs__clear {
      margin-right: 4px;
    }
    
    .vs__open-indicator {
      fill: #7367f0;
      transition: transform 0.3s ease;
    }
    
    .vs__dropdown-toggle--open .vs__open-indicator {
      transform: rotate(180deg);
    }
  }
}
</style>